import { useState, useEffect, useMemo } from 'react';
import { Outlet, Link } from "react-router-dom";
import { 
  Navbar, 
  NavbarBrand, 
  NavbarContent,
  NavbarItem,
  NavbarMenuToggle,
  NavbarMenu,
  NavbarMenuItem,
} from "@heroui/react";
import { useTheme } from "@heroui/use-theme";
import { ThemeSwitcher } from "./components/shared/ThemeSwitcher";
import LoginModal from "./components/shared/LoginModal";
import UserMenu from "./components/shared/UserMenu";
import LanguageSwitcher from "./components/shared/LanguageSwitcher";
import { useAuthStore, validateAuthState } from './stores/auth.store';
import ApiClient from './services/api/axios/apiClient';

import logoMacroindustrialTr from '@/assets/logo-macroindustrial-tr.png';
import logoMacroindustrial from '@/assets/logo-macroindustrial.png';

import menuItems from './utils/modulos';
import { generateStateKey } from './utils/stateKeyGenerator';

export default function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isLoggedIn, user } = useAuthStore();
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    // Validate the auth state when the component mounts
    const isValid = validateAuthState();
    if (!isValid) {
      // If token is expired or invalid, ensure it's cleared from store
      useAuthStore.getState().clearCredentials();
    } else if (isValid && !useAuthStore.getState().user) {
      // If token is valid but user data is not loaded, fetch user profile
      fetchUserProfile();
    }
  }, []);

  const logoSrc = useMemo(() => {
    return theme === 'dark' 
      ? logoMacroindustrialTr
      : logoMacroindustrial;
  }, [theme]);

  const handleThemeChange = (newTheme: 'light' | 'dark') => {
    setTheme(newTheme);
  };

  const fetchUserProfile = async () => {
    try {
      const response = await ApiClient.get('/api/users/me');
      const userData = response.data;
      useAuthStore.getState().updateUser(userData);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // If error occurs, clear credentials to avoid stuck in logged state
      useAuthStore.getState().clearCredentials();
    }
  };

  const handleGoogleLogin = () => {
    // Generate a new state key and redirect to Google auth endpoint
    generateStateKey();
    window.location.href = `${import.meta.env.VITE_API_URL}/connect/google`;
  };

  return (
    <div>
      {(isLoggedIn && user?.empresa) && (
        <Navbar onMenuOpenChange={setIsMenuOpen}>
          <NavbarContent>
            <NavbarMenuToggle
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              className="sm:hidden"
            />
            <NavbarBrand>
              <Link to="/dashboard">
                <img 
                  src={logoSrc} 
                  alt="Macroindustrial Logo" 
                  className='rounded-md'
                  style={{minWidth:"200px"}}
                />
              </Link>
            </NavbarBrand>
          </NavbarContent>

          <NavbarContent className="hidden sm:flex gap-4" justify="center">
            {menuItems.map((item) => (
              <NavbarItem key={item.name}>
                <Link 
                  color="foreground" 
                  to={item.href}
                  className="text-foreground"
                >
                  {item.name}
                </Link>
              </NavbarItem>
            ))}
          </NavbarContent>

          <NavbarContent justify="end">
            <NavbarItem className="hidden lg:flex">
              <ThemeSwitcher onThemeChange={handleThemeChange} />
            </NavbarItem>
            <NavbarItem className="hidden lg:flex">
              <LanguageSwitcher />
            </NavbarItem>
            <NavbarItem className="hidden lg:flex">
              {isLoggedIn ? (
                <UserMenu />
              ) : (
                <LoginModal 
                  onGoogleLogin={handleGoogleLogin}
                  enableGoogle={true}
                  enableFacebook={false}
                  enableMicrosoft={false}
                  showUsername={false}
                  className="text-white"
                  isLoggedInProp={isLoggedIn}
                />
              )}
            </NavbarItem>
          </NavbarContent>

          <NavbarMenu>
            {menuItems.map((item, index) => (
              <NavbarMenuItem key={`${item.name}-${index}`}>
                <Link 
                  className="w-full" 
                  to={item.href}
                >
                  {item.name}
                </Link>
              </NavbarMenuItem>
            ))}
            <NavbarMenuItem>
              <div className="w-full flex justify-between items-center">
                <ThemeSwitcher onThemeChange={handleThemeChange} />
                <LanguageSwitcher />
                {isLoggedIn ? (
                  <UserMenu />
                ) : (
                  <LoginModal 
                    onGoogleLogin={handleGoogleLogin}
                    enableGoogle={true}
                    enableFacebook={false}
                    enableMicrosoft={false}
                    className="text-white"
                    showUsername={false}
                    isLoggedInProp={isLoggedIn}
                  />
                )}
              </div>
            </NavbarMenuItem>
          </NavbarMenu>
        </Navbar>
      )}

      <div className="container mx-auto">
        <Outlet />
      </div>
    </div>
  );
}
